// Review's data: the Chapter query and the five calls that change a Chapter.
//
// The Chapter view is the unit of truth. Every mutation answers with the whole
// ChapterReview, so an edit updates the version list, the stats, and the
// approval in one write and the screen never refetches to find out what its
// own call did. The Book is not asked for here at all: BookLayout owns that
// one query for the whole page and hands the payload down through context.

import type { ChapterReview, Sample, SampleRequest, ScriptVersionId } from "@contract";
import { buildPath } from "@contract";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { api } from "../../../api/fetcher";
import { queryKeys } from "../../../api/keys";
import { POLL_INTERVAL_MS } from "../../../api/poll";

const chapterPath = (bookId: string, chapterId: string) =>
  buildPath("/books/:id/chapters/:cid", { id: bookId, cid: chapterId });

/**
 * A Chapter is worth re-asking about while something is happening to it, and
 * `pending` counts: the Chapter is waiting for a Refinement the worker has not
 * started yet, and nothing else would ever tell this screen it did. Without the
 * poll the rail would go to "freigegeben" with a character count while the main
 * column still read "wartet auf die Überarbeitung".
 */
export const chapterIsBusy = (chapter: ChapterReview | undefined): boolean =>
  chapter !== undefined &&
  (chapter.activeJob?.kind === "quality" ||
    chapter.lock !== null ||
    chapter.state === "pending" ||
    chapter.state === "refining" ||
    chapter.state === "rendering");

export function useChapterReview(
  bookId: string,
  chapterId: string | undefined,
  versionId?: ScriptVersionId,
) {
  return useQuery({
    queryKey: queryKeys.chapterReview(bookId, chapterId ?? "", versionId),
    queryFn: () => {
      const path = chapterPath(bookId, chapterId as string);
      return api<ChapterReview>(
        versionId ? `${path}?versionId=${encodeURIComponent(versionId)}` : path,
      );
    },
    enabled: chapterId !== undefined && chapterId !== "",
    refetchInterval: (query) => (chapterIsBusy(query.state.data) ? POLL_INTERVAL_MS : false),
  });
}

export interface BlockEdit {
  readonly blockId: string;
  /** The version the text was read from: the server forks on the first edit of it. */
  readonly versionId: ScriptVersionId;
  readonly text: string;
}

/**
 * Committing an edit. The Block shows the new text at once and the row keeps
 * its place, because waiting a round trip to see your own typing is what makes
 * an editor feel broken; a refusal puts the old text back and the caller
 * surfaces why.
 */
export function useEditBlock(bookId: string, chapterId: string) {
  const client = useQueryClient();
  const key = queryKeys.chapterReview(bookId, chapterId);
  return useMutation({
    mutationFn: (edit: BlockEdit) =>
      api<ChapterReview>(
        buildPath("/books/:id/chapters/:cid/blocks/:bid", {
          id: bookId,
          cid: chapterId,
          bid: edit.blockId,
        }),
        { method: "PATCH", body: { versionId: edit.versionId, text: edit.text } },
      ),
    onMutate: async (edit) => {
      await client.cancelQueries({ queryKey: key });
      const previous = client.getQueryData<ChapterReview>(key);
      if (previous) client.setQueryData(key, withBlockText(previous, edit));
      return { previous };
    },
    onError: (_error, _edit, context) => {
      if (context?.previous) client.setQueryData(key, context.previous);
    },
    onSuccess: (review) => {
      client.setQueryData(key, review);
      void client.invalidateQueries({ queryKey: queryKeys.book(bookId) });
    },
  });
}

/**
 * The optimistic copy. Character counts move with the text so the rail's
 * ZEICHEN column and the header's stats do not read a number the Author can
 * see is wrong; everything else waits for the answer, because only the server
 * knows whether this edit forked a version.
 */
function withBlockText(review: ChapterReview, edit: BlockEdit): ChapterReview {
  const script = review.script;
  if (!script) return review;
  const characterCount = [...edit.text].length;
  const patch = <T extends { id: string; text: string; characterCount: number }>(block: T): T =>
    block.id === edit.blockId
      ? { ...block, text: edit.text, characterCount, editedByAuthor: true }
      : block;
  return {
    ...review,
    script: {
      ...script,
      blocks: script.blocks.map(patch),
      pairs: script.pairs.map((pair) => ({ ...pair, block: patch(pair.block) })),
    },
  };
}

/** A call that answers with the whole Chapter: approve, re-run, restore. */
function useChapterCall<T>(bookId: string, chapterId: string, path: string) {
  const client = useQueryClient();
  const key = queryKeys.chapterReview(bookId, chapterId);
  return useMutation({
    mutationFn: (body: T) => api<ChapterReview>(path, { method: "POST", body }),
    // The read in flight is cancelled before the call goes out, so a poll that
    // left before the mutation cannot land after it and put the version the
    // Author just replaced back on screen while the server holds the new one.
    onMutate: () => client.cancelQueries({ queryKey: key }),
    onSuccess: (review) => {
      client.setQueryData(key, review);
      void client.invalidateQueries({ queryKey: queryKeys.book(bookId) });
    },
  });
}

export function useApproveChapter(bookId: string, chapterId: string) {
  return useChapterCall<{ versionId: ScriptVersionId; acknowledgeDroppedNodes: boolean }>(
    bookId,
    chapterId,
    buildPath("/books/:id/chapters/:cid/approve", { id: bookId, cid: chapterId }),
  );
}

export function useRerunChapter(bookId: string, chapterId: string) {
  return useChapterCall<{ instruction: string | null }>(
    bookId,
    chapterId,
    buildPath("/books/:id/chapters/:cid/rerun", { id: bookId, cid: chapterId }),
  );
}

export function useRestoreVersion(bookId: string, chapterId: string) {
  return useChapterCall<{ versionId: ScriptVersionId }>(
    bookId,
    chapterId,
    buildPath("/books/:id/chapters/:cid/restore", { id: bookId, cid: chapterId }),
  );
}

/**
 * Stopping this Chapter's render. It answers with render progress rather than
 * the Chapter, so both queries are invalidated: the lock lifts on the next
 * read and the rail's state follows.
 */
export function useStopChapterRender(bookId: string, chapterId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api<unknown>(
        buildPath("/books/:id/chapters/:cid/render/stop", { id: bookId, cid: chapterId }),
        { method: "POST" },
      ),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.book(bookId) });
      void client.invalidateQueries({ queryKey: queryKeys.chapterReview(bookId, chapterId) });
    },
  });
}

/** Retrying a Refinement the worker has given up on. */
export function useRetryJob(bookId: string, chapterId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (jobId: string) =>
      api<unknown>(buildPath("/jobs/:id/retry", { id: jobId }), { method: "POST" }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.book(bookId) });
      void client.invalidateQueries({ queryKey: queryKeys.chapterReview(bookId, chapterId) });
    },
  });
}

/**
 * Hearing the focused Block. The route answers 200 for a Sample this Voice has
 * spoken before and 202 for one it has to render, so the caller polls the
 * Sample until it is cached; a second press on the same Block is free, which
 * is the point of the cache.
 */
export function useSample(bookId: string) {
  const client = useQueryClient();
  const request = useMutation({
    mutationFn: (body: SampleRequest) =>
      api<Sample>(buildPath("/books/:id/samples", { id: bookId }), { method: "POST", body }),
    onSuccess: (sample) => client.setQueryData(queryKeys.sample(sample.id), sample),
  });
  const poll = useCallback(
    (sampleId: string) =>
      client.fetchQuery({
        queryKey: queryKeys.sample(sampleId),
        queryFn: () => api<Sample>(buildPath("/samples/:id", { id: sampleId })),
        staleTime: 0,
      }),
    [client],
  );
  return { request, poll };
}

/** Where the browser fetches a rendered Sample's audio from. */
export const sampleAudioUrl = (sampleId: string): string =>
  `/api${buildPath("/samples/:id/audio", { id: sampleId })}`;
